
import { supabase } from '@/integrations/supabase/client';

export const useCalendarOperations = () => {
  const fetchCalendarEvents = async (startDate: Date, endDate: Date) => {
    try {
      const response = await supabase.functions.invoke('calendar-list', {
        body: {
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      });
      return response.data?.events || [];
    } catch (error) {
      console.error('Calendar events fetch error:', error);
      throw error;
    }
  };

  const createCalendarEvent = async (eventData: any) => {
    try {
      const response = await supabase.functions.invoke('calendar-create', {
        body: eventData
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    } catch (error) {
      console.error('Calendar event creation error:', error);
      throw error;
    }
  };

  const updateCalendarEvent = async (eventId: string, updates: any) => {
    try {
      const response = await supabase.functions.invoke('calendar-update', {
        body: { id: eventId, updates }
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    } catch (error) {
      console.error('Calendar event update error:', error);
      throw error;
    }
  };

  const deleteCalendarEvent = async (eventId: string, notifyAttendees?: boolean) => {
    try {
      const response = await supabase.functions.invoke('calendar-delete', {
        body: { id: eventId, notify_attendees: notifyAttendees }
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    } catch (error) {
      console.error('Calendar event deletion error:', error);
      throw error;
    }
  };

  const checkCalendarAvailability = async (start: Date, end: Date, durationMinutes: number, userIds?: string[]) => {
    try {
      const response = await supabase.functions.invoke('calendar-availability', {
        body: { 
          start_time: start.toISOString(), 
          end_time: end.toISOString(),
          duration_minutes: durationMinutes,
          user_ids: userIds
        }
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data?.availability || [];
    } catch (error) {
      console.error('Calendar availability check error:', error);
      throw error;
    }
  };

  const suggestMeetingTimes = async (params: {
    duration_minutes: number;
    preferred_days?: string[];
    preferred_time_ranges?: { start: string; end: string }[];
    attendees?: string[];
    limit?: number;
  }) => {
    try {
      // Calculate suggested time range (next 2 weeks)
      const now = new Date();
      const start = new Date(now);
      start.setHours(9, 0, 0, 0); // Start at 9 AM today
      const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 2 weeks from now
      end.setHours(17, 0, 0, 0); // End at 5 PM

      const availability = await checkCalendarAvailability(
        start, 
        end, 
        params.duration_minutes, 
        params.attendees
      );

      // Filter and rank suggestions based on preferences
      let suggestions = availability
        .flatMap((userAvail: any) => userAvail.available_slots)
        .filter((slot: any) => slot.is_available);

      // Apply day preferences
      if (params.preferred_days?.length) {
        suggestions = suggestions.filter((slot: any) => {
          const dayName = new Date(slot.start).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
          return params.preferred_days?.includes(dayName);
        });
      }

      // Apply time range preferences
      if (params.preferred_time_ranges?.length) {
        suggestions = suggestions.filter((slot: any) => {
          const slotTime = new Date(slot.start).toTimeString().substring(0, 5);
          return params.preferred_time_ranges?.some(range => 
            slotTime >= range.start && slotTime <= range.end
          );
        });
      }

      // Sort by date and limit results
      suggestions.sort((a: any, b: any) => new Date(a.start).getTime() - new Date(b.start).getTime());
      
      return suggestions.slice(0, params.limit || 10);
    } catch (error) {
      console.error('Meeting time suggestion error:', error);
      throw error;
    }
  };

  return {
    fetchCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
    checkCalendarAvailability,
    suggestMeetingTimes
  };
};
